import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  Alert,
  CircularProgress,
  Tooltip,
  useTheme,
  useMediaQuery,
  alpha,
  Stack,
  Paper,
  Divider,
  Collapse,
  Avatar as MuiAvatar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import funcionarioService, { FuncionarioFilters } from '../services/funcionarioService';
import { IFuncionario } from '../types';
import FuncionarioForm from '../components/forms/FuncionarioForm';
import { toast } from 'react-toastify';
import ProfileAvatar from '../components/common/Avatar';
import SysKpiCard from '../components/common/SysKpiCard';
import {
  pageShellSx,
  rankingHeaderPaperSx,
  filtersPaperSx,
  listSectionPaperSx,
  listSectionHeaderBarSx,
  rankingTableHeadRowSx,
  tablePaginationWrapperSx,
} from '../styles/theme';

const FuncionariosPage: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [funcionarios, setFuncionarios] = useState<IFuncionario[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });
  const [filters, setFilters] = useState<FuncionarioFilters>({
    search: '',
    status: '',
    cargo: '',
    estado: '',
    page: 1,
    limit: 10,
    sortBy: 'createdAt',
    sortOrder: 'DESC'
  });

  // Estados para modais
  const [openForm, setOpenForm] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedFuncionario, setSelectedFuncionario] = useState<IFuncionario | null>(null);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');

  // Estados para filtros
  const [showFilters, setShowFilters] = useState(false);

  // Carregar funcionários
  const loadFuncionarios = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await funcionarioService.getFuncionarios(filters);

      if (response.success) {
        setFuncionarios(response.data || []);
        setPagination(response.pagination || {
          page: 1,
          limit: 10,
          total: 0,
          pages: 0
        });
      } else {
        setError(response.message || 'Erro ao carregar funcionários');
      }
    } catch (err) {
      setError('Erro ao carregar funcionários');
      console.error('Erro ao carregar funcionários:', err);
    } finally {
      setLoading(false);
    }
  };

  // Carregar dados iniciais
  useEffect(() => {
    loadFuncionarios();
  }, [filters]);

  // Aplicar filtros
  const handleFilterChange = (field: keyof FuncionarioFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: 1 // Reset para primeira página
    }));
  };

  // Limpar filtros
  const clearFilters = () => {
    setFilters({
      search: '',
      status: '',
      cargo: '',
      estado: '',
      page: 1,
      limit: 10,
      sortBy: 'createdAt',
      sortOrder: 'DESC'
    });
  };

  // Abrir modal de criação
  const handleCreate = () => {
    setFormMode('create');
    setSelectedFuncionario(null);
    setOpenForm(true);
  };

  // Abrir modal de edição
  const handleEdit = (funcionario: IFuncionario) => {
    setFormMode('edit');
    setSelectedFuncionario(funcionario);
    setOpenForm(true);
  };

  // Abrir modal de visualização
  const handleView = (funcionario: IFuncionario) => {
    setSelectedFuncionario(funcionario);
    // Implementar modal de visualização se necessário
  };

  // Abrir modal de exclusão
  const handleDeleteClick = (funcionario: IFuncionario) => {
    setSelectedFuncionario(funcionario);
    setOpenDelete(true);
  };

  // Confirmar exclusão
  const handleDeleteConfirm = async () => {
    if (!selectedFuncionario) return;

    try {
      const response = await funcionarioService.deleteFuncionario(selectedFuncionario.id);

      if (response.success) {
        toast.success('Funcionário excluído com sucesso');
        loadFuncionarios();
        setOpenDelete(false);
        setSelectedFuncionario(null);
      } else {
        toast.error(response.message || 'Erro ao excluir funcionário');
      }
    } catch (err) {
      toast.error('Erro ao excluir funcionário');
      console.error('Erro ao excluir funcionário:', err);
    }
  };

  // Alterar status
  const handleStatusChange = async (funcionario: IFuncionario, newStatus: string) => {
    try {
      const response = await funcionarioService.toggleStatus(
        funcionario.id,
        newStatus as 'ativo' | 'inativo' | 'demitido' | 'aposentado'
      );

      if (response.success) {
        toast.success(`Status alterado para ${newStatus}`);
        loadFuncionarios();
      } else {
        toast.error(response.message || 'Erro ao alterar status');
      }
    } catch (err) {
      toast.error('Erro ao alterar status');
      console.error('Erro ao alterar status:', err);
    }
  };

  // Fechar modal de formulário
  const handleFormClose = () => {
    setOpenForm(false);
    setSelectedFuncionario(null);
  };

  // Sucesso no formulário
  const handleFormSuccess = () => {
    handleFormClose();
    loadFuncionarios();
  };

  // Obter cor do status
  const getStatusColor = (status: string) => {
    return funcionarioService.getStatusColor(status);
  };

  // Obter cor do tipo de contrato
  const getContractTypeColor = (tipo: string) => {
    return funcionarioService.getContractTypeColor(tipo);
  };

  // Formatar salário
  const formatSalary = (salary: number) => {
    return funcionarioService.formatSalary(salary);
  };

  // Formatar data
  const formatDate = (date: string) => {
    return funcionarioService.formatDate(date);
  };

  // Formatar CPF
  const formatCpf = (cpf: string) => {
    return funcionarioService.formatCpf(cpf);
  };

  // Formatar telefone
  const formatPhone = (phone: string) => {
    return funcionarioService.formatPhone(phone);
  };

  if (!user || (user.perfil !== 'administrador' && user.perfil !== 'financeiro')) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Você não tem permissão para acessar esta página.
        </Alert>
      </Box>
    );
  }

  const totalAtivos = funcionarios.filter((f) => f.status === 'ativo').length;

  return (
    <Box sx={pageShellSx}>
      <Paper elevation={0} sx={rankingHeaderPaperSx(theme, 'info')}>
        <Box sx={{ position: 'absolute', top: -20, right: -10, opacity: 0.08, transform: 'rotate(15deg)' }}>
          <PersonIcon sx={{ fontSize: 120, color: theme.palette.info.main }} />
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1.25}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <MuiAvatar sx={{ width: 40, height: 40, background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`, boxShadow: `0 4px 12px ${alpha(theme.palette.info.main, 0.35)}` }}>
              <PersonIcon sx={{ fontSize: 22 }} />
            </MuiAvatar>
            <Box>
              <Typography variant="h6" fontWeight={800} lineHeight={1.2}>Gerenciamento de Funcionários</Typography>
              <Typography variant="caption" color="text.secondary">Gerencie os funcionários do sistema</Typography>
            </Box>
          </Stack>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleCreate} fullWidth={isMobile}>Novo Funcionário</Button>
        </Stack>
      </Paper>

      {!loading && pagination.total > 0 && (
        <Grid container spacing={1} mb={1.5}>
          <Grid item xs={6} md={4}><SysKpiCard titulo="Total" valor={pagination.total} icone={<GroupsIcon sx={{ color: theme.palette.primary.main }} />} cor={theme.palette.primary.main} bgCor={alpha(theme.palette.primary.main, 0.12)} /></Grid>
          <Grid item xs={6} md={4}><SysKpiCard titulo="Ativos" valor={totalAtivos} subtitulo={`${pagination.total ? Math.round((totalAtivos / pagination.total) * 100) : 0}% do total`} icone={<CheckCircleIcon sx={{ color: theme.palette.success.main }} />} cor={theme.palette.success.main} bgCor={alpha(theme.palette.success.main, 0.12)} /></Grid>
          <Grid item xs={6} md={4}><SysKpiCard titulo="Nesta Página" valor={funcionarios.length} icone={<PersonIcon sx={{ color: theme.palette.info.main }} />} cor={theme.palette.info.main} bgCor={alpha(theme.palette.info.main, 0.12)} /></Grid>
        </Grid>
      )}

      <Paper elevation={0} sx={filtersPaperSx(theme)}>
        <Stack direction="row" alignItems="center" spacing={0.75} mb={showFilters ? 1 : 0}>
          <FilterIcon color="primary" sx={{ fontSize: 18 }} />
          <Typography variant="body2" fontWeight={700}>Filtros</Typography>
          <Button size="small" onClick={() => setShowFilters(!showFilters)} sx={{ ml: 'auto', textTransform: 'none' }}>{showFilters ? 'Ocultar' : 'Mostrar'}</Button>
        </Stack>
        <Grid container spacing={1}>
          <Grid item xs={12} md={4}>
            <TextField fullWidth size="small" label="Buscar" placeholder="Nome, CPF, email ou cargo" value={filters.search} onChange={(e) => handleFilterChange('search', e.target.value)} InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} /> }} />
          </Grid>
          <Collapse in={showFilters} sx={{ width: '100%' }}>
            <Grid container spacing={1} sx={{ px: 0.5, pt: 1 }}>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small"><InputLabel>Status</InputLabel><Select value={filters.status} onChange={(e) => handleFilterChange('status', e.target.value)} label="Status"><MenuItem value="">Todos</MenuItem><MenuItem value="ativo">Ativo</MenuItem><MenuItem value="inativo">Inativo</MenuItem><MenuItem value="demitido">Demitido</MenuItem><MenuItem value="aposentado">Aposentado</MenuItem></Select></FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small"><InputLabel>Cargo</InputLabel><Select value={filters.cargo} onChange={(e) => handleFilterChange('cargo', e.target.value)} label="Cargo"><MenuItem value="">Todos</MenuItem><MenuItem value="Desenvolvedor">Desenvolvedor</MenuItem><MenuItem value="Analista">Analista</MenuItem><MenuItem value="Gerente">Gerente</MenuItem><MenuItem value="Tecnico">Técnico</MenuItem><MenuItem value="Suporte">Suporte</MenuItem><MenuItem value="Vendedor">Vendedor</MenuItem></Select></FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small"><InputLabel>Estado</InputLabel><Select value={filters.estado} onChange={(e) => handleFilterChange('estado', e.target.value)} label="Estado"><MenuItem value="">Todos</MenuItem><MenuItem value="SP">São Paulo</MenuItem><MenuItem value="RJ">Rio de Janeiro</MenuItem><MenuItem value="MG">Minas Gerais</MenuItem><MenuItem value="RS">Rio Grande do Sul</MenuItem><MenuItem value="PR">Paraná</MenuItem><MenuItem value="SC">Santa Catarina</MenuItem></Select></FormControl>
              </Grid>
              <Grid item xs={12} md={3}><Button variant="outlined" size="small" onClick={clearFilters} fullWidth>Limpar</Button></Grid>
            </Grid>
          </Collapse>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={listSectionPaperSx(theme)}>
        <Box sx={listSectionHeaderBarSx(theme)}>
          <Typography variant="body2" fontWeight={700}>Listagem de Funcionários</Typography>
          {!loading && <Chip size="small" label={`${pagination.total} funcionário${pagination.total !== 1 ? 's' : ''}`} color="primary" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5, gap: 1 }}><CircularProgress size={32} /><Typography variant="caption" color="text.secondary">Carregando funcionários...</Typography></Box>
        ) : error ? (
          <Box sx={{ p: 2 }}><Alert severity="error">{error}</Alert></Box>
        ) : funcionarios.length === 0 ? (
          <Box sx={{ p: 2.5, textAlign: 'center' }}>
            <PersonIcon sx={{ fontSize: 40, color: alpha(theme.palette.text.secondary, 0.25), mb: 1 }} />
            <Typography variant="subtitle2" fontWeight={600}>Nenhum funcionário encontrado</Typography>
          </Box>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 520 }}>
              <Table size="small" stickyHeader>
                <TableHead><TableRow sx={rankingTableHeadRowSx(theme)}>
                  <TableCell>Foto</TableCell>
                  <TableCell>Nome</TableCell>
                  <TableCell>CPF</TableCell>
                  <TableCell>Cargo</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Tipo Contrato</TableCell>
                  <TableCell>Salário</TableCell>
                  <TableCell>Data Admissão</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
                </TableHead>
                <TableBody>
                  {funcionarios.map((funcionario) => (
                    <TableRow key={funcionario.id} hover sx={{ '& .MuiTableCell-root': { py: 0.6, px: 1 }, '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) } }}>
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                        <ProfileAvatar
                          src={funcionarioService.getAvatarUrl(funcionario.foto)}
                          size={32}
                          showUploadButton={false}
                          editable={false}
                          sx={{
                            mx: 'auto',
                            border: '2px solid white',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            borderRadius: '50%',
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontSize: '0.8rem' }}>
                            {funcionario.nome}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            {funcionario.email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{formatCpf(funcionario.cpf)}</TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{funcionario.cargo}</TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                        <Chip
                          label={funcionario.status}
                          color={getStatusColor(funcionario.status) as any}
                          size="small"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                        <Chip
                          label={funcionario.tipo_contrato}
                          color={getContractTypeColor(funcionario.tipo_contrato) as any}
                          size="small"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{formatSalary(funcionario.salario)}</TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{formatDate(funcionario.data_admissao)}</TableCell>
                      <TableCell align="center" sx={{ py: 0.5 }}>
                        <Box display="flex" gap={0.5} justifyContent="center">
                          <Tooltip title="Visualizar">
                            <IconButton
                              size="small"
                              onClick={() => handleView(funcionario)}
                              sx={{ p: 0.5 }}
                            >
                              <ViewIcon sx={{ fontSize: '1rem' }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Editar">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(funcionario)}
                              sx={{ p: 0.5 }}
                            >
                              <EditIcon sx={{ fontSize: '1rem' }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Excluir">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(funcionario)}
                              sx={{ p: 0.5 }}
                            >
                              <DeleteIcon sx={{ fontSize: '1rem' }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {funcionarios.length > 0 && (
              <>
                <Divider />
                <Box sx={{ ...tablePaginationWrapperSx(theme), flexDirection: 'column', gap: 1 }}>
                  {pagination.pages > 1 && <Pagination count={pagination.pages} page={pagination.page} onChange={(_, page) => handleFilterChange('page', page)} color="primary" size="small" />}
                  <Typography variant="caption" color="text.secondary">Mostrando {funcionarios.length} de {pagination.total} funcionários</Typography>
                </Box>
              </>
            )}
          </>
        )}
      </Paper>

      {/* Modal de Formulário */}
      <Dialog
        open={openForm}
        onClose={handleFormClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {formMode === 'create' ? 'Novo Funcionário' : 'Editar Funcionário'}
        </DialogTitle>
        <DialogContent>
          <FuncionarioForm
            funcionario={selectedFuncionario}
            mode={formMode}
            onSuccess={handleFormSuccess}
            onCancel={handleFormClose}
          />
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmação de Exclusão */}
      <Dialog
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir o funcionário{' '}
            <strong>{selectedFuncionario?.nome}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDelete(false)} size="small" sx={{ fontSize: '0.8rem' }}>
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            size="small"
            sx={{ fontSize: '0.8rem' }}
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FuncionariosPage; 